import React from 'react';
import { Mail, Check, FileText } from 'lucide-react';
import { TIERS } from '../utils/tiers';
import TierCrest from '../components/stamps/TierCrest';
import { useLocalizedNavigate } from '../i18n/useLocalizedNav';

const SECTIONS = [
  { id: 'summary', label: 'Plain-language summary' },
  { id: 'account', label: 'Your passport account' },
  { id: 'claim', label: 'Stamping & claim codes' },
  { id: 'tiers', label: 'Tiers & discounts' },
  { id: 'acceptable', label: 'Acceptable use' },
  { id: 'vendor', label: 'Vendor relationship' },
  { id: 'ip', label: 'Intellectual property' },
  { id: 'liability', label: 'Disclaimers & liability' },
  { id: 'law', label: 'Governing law' },
];

const SUMMARY = [
  'You must be 16+ (or have guardian consent) to create a passport.',
  'One stamp per visit, verified by the venue staff code.',
  "Don't fake visits — fraud forfeits your stamps.",
  'We can pause an account if we see abuse.',
  'Discounts apply only at participating venues.',
  'Armenian law governs these Terms.',
];

const Terms: React.FC = () => {
  const navigate = useLocalizedNavigate();

  return (
    <div className="space-y-12">
      {/* Hero */}
      <section className="-mx-4 px-6 lg:px-10 py-12 bg-brand-secondary text-white rounded-3xl space-y-5">
        <p className="font-heading font-bold text-xs tracking-[0.25em] text-brand-accent uppercase">
          Document · 02
        </p>
        <h1 className="font-display text-4xl md:text-6xl leading-[1.05] text-warm-100/90">
          The fine print,{' '}
          <span className="italic text-brand-primary">in plain ink</span>.
        </h1>
        <p className="text-warm-500 max-w-2xl leading-relaxed">
          Aranor is free, opt-in, and built on a simple promise: visit a
          verified Armenian venue, get a stamp, climb the ranks, unlock a
          real-world discount. These are the rules of that exchange.
        </p>
        <div className="flex flex-wrap gap-3 text-[10px] font-heading font-bold tracking-[0.15em] uppercase">
          <span className="px-3 py-1.5 bg-white/5 rounded-lg text-warm-500">
            Last updated · May 19 · 2026
          </span>
          <span className="px-3 py-1.5 bg-white/5 rounded-lg text-warm-500">
            Effective · Jun 01 · 2026
          </span>
          <span className="px-3 py-1.5 bg-brand-primary text-brand-secondary rounded-lg">
            Terms of Service
          </span>
        </div>
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-10">
        {/* Sidebar */}
        <aside className="hidden lg:block space-y-6 h-fit sticky top-24">
          <div className="space-y-2">
            <p className="font-heading font-bold text-[11px] tracking-[0.2em] text-warm-500 uppercase">
              On this page
            </p>
            <ul className="space-y-1">
              {SECTIONS.map((s, i) => (
                <li key={s.id}>
                  <a
                    href={`#${s.id}`}
                    className="block text-sm text-warm-600 hover:text-brand-accent transition-colors py-1"
                  >
                    <span className="text-warm-400 mr-2">
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    {s.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-brand-secondary text-white rounded-2xl p-5 space-y-1">
            <p className="font-heading font-bold text-[10px] tracking-[0.2em] text-brand-accent uppercase">
              Need a human?
            </p>
            <a
              href="mailto:contact@aranortravel.com"
              className="flex items-start gap-2 font-heading font-bold text-sm text-brand-primary break-all"
            >
              <Mail className="w-4 h-4 mt-0.5 shrink-0" />
              <span className="break-all">contact@aranortravel.com</span>
            </a>
            <p className="text-warm-500 text-xs">
              We reply within 3 business days, in EN / HY / RU.
            </p>
          </div>
        </aside>

        {/* Body */}
        <div className="space-y-14 max-w-3xl">
          <section id="summary" className="scroll-mt-24 space-y-4">
            <p className="font-heading font-bold text-xs tracking-[0.2em] text-brand-accent uppercase">
              § 01 · TL;DR
            </p>
            <h2 className="font-display text-3xl text-warm-800">
              Plain-language summary
            </h2>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {SUMMARY.map((line) => (
                <li
                  key={line}
                  className="flex items-start gap-3 bg-white border border-warm-200/70 rounded-xl p-4 text-sm text-warm-700"
                >
                  <span className="w-5 h-5 shrink-0 rounded-full bg-brand-primary/20 flex items-center justify-center mt-0.5">
                    <Check className="w-3 h-3 text-brand-secondary" />
                  </span>
                  {line}
                </li>
              ))}
            </ul>
            <p className="text-warm-600 leading-relaxed">
              By creating a passport you accept these Terms. If you don't,
              please don't sign up — and feel free to keep browsing the public
              locations directory anyway.
            </p>
          </section>

          <section id="account" className="scroll-mt-24 space-y-4">
            <p className="font-heading font-bold text-xs tracking-[0.2em] text-brand-accent uppercase">
              § 02 · Account
            </p>
            <h2 className="font-display text-3xl text-warm-800">
              Your passport account
            </h2>
            <p className="text-warm-600 leading-relaxed">
              Each passport is personal and non-transferable. You're responsible
              for keeping your password secret and for everything that happens
              under your account. If you suspect someone else has access, change
              your password and contact us — we'll review and roll back any
              fraudulent stamps.
            </p>
            <div className="bg-brand-primary/10 border border-brand-primary/20 rounded-2xl p-5">
              <p className="font-bold text-warm-800 text-sm">
                One human, one passport
              </p>
              <p className="text-warm-600 text-sm leading-relaxed mt-1">
                Accounts created only to farm stamps may be merged or removed.
              </p>
            </div>
          </section>

          <section id="claim" className="scroll-mt-24 space-y-4">
            <p className="font-heading font-bold text-xs tracking-[0.2em] text-brand-accent uppercase">
              § 03 · The loop
            </p>
            <h2 className="font-display text-3xl text-warm-800">
              Stamping &amp; claim codes
            </h2>
            <p className="text-warm-600">
              A stamp is recorded when, in this order:
            </p>
            <ol className="space-y-2 text-warm-700 list-decimal list-inside">
              <li>
                You open the claim screen in the app while at the venue and
                generate your QR.
              </li>
              <li>
                A venue staff member verifies your visit with their venue code.
              </li>
              <li>
                The stamp is sealed to your passport and your multiplier
                increases.
              </li>
            </ol>
            <p className="text-warm-500 text-sm leading-relaxed">
              Each venue grants exactly one stamp; visiting again — or another
              venue that grants the same stamp — increases your multiplier.
            </p>
          </section>

          <section id="tiers" className="scroll-mt-24 space-y-4">
            <p className="font-heading font-bold text-xs tracking-[0.2em] text-brand-accent uppercase">
              § 04 · Rewards
            </p>
            <h2 className="font-display text-3xl text-warm-800">
              Tiers &amp; discounts
            </h2>
            <p className="text-warm-600 leading-relaxed">
              Stamps grow your rank — Junior, Explorer, Seasoned, Master, Guru
              of Armenia. Each tier can unlock a deeper discount band at
              participating venues. Discounts are honored by the venues, not by
              Aranor — we don't process payments.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              {TIERS.map((tier) => (
                <div
                  key={tier.short}
                  className={`rounded-xl p-4 flex flex-col items-center text-center gap-2 border ${
                    tier.top
                      ? 'bg-brand-secondary border-brand-secondary'
                      : 'bg-white border-warm-200/60'
                  }`}
                >
                  <TierCrest
                    color={tier.crest}
                    label={tier.short}
                    className="w-10 h-12"
                  />
                  <p
                    className={`font-display text-sm ${
                      tier.top ? 'text-brand-primary' : 'text-warm-800'
                    }`}
                  >
                    {tier.name.replace(' Member', '')}
                  </p>
                  <p
                    className={`font-heading font-bold text-[9px] tracking-[0.1em] uppercase ${
                      tier.top ? 'text-warm-400' : 'text-warm-500'
                    }`}
                  >
                    {tier.range}
                  </p>
                </div>
              ))}
            </div>
          </section>

          <section id="acceptable" className="scroll-mt-24 space-y-4">
            <p className="font-heading font-bold text-xs tracking-[0.2em] text-brand-accent uppercase">
              § 05 · Do not
            </p>
            <h2 className="font-display text-3xl text-warm-800">
              Acceptable use
            </h2>
            <ul className="space-y-3 text-warm-700">
              <li>
                No automated stamping — bots, scripts, or coordinated
                multi-account farming.
              </li>
              <li>
                No selling, trading, or transferring stamps, ranks, or accounts.
              </li>
              <li>
                No harassment of venue staff over a denied stamp — contact
                support instead.
              </li>
              <li>
                No reverse-engineering of the claim-code mechanism.
              </li>
            </ul>
            <div className="bg-brand-danger/10 border border-brand-danger/20 rounded-2xl p-5">
              <p className="font-bold text-warm-800 text-sm">If you break this</p>
              <p className="text-warm-600 text-sm leading-relaxed mt-1">
                We may roll back affected stamps and, for repeated or
                coordinated abuse, close the account.
              </p>
            </div>
          </section>

          <section id="vendor" className="scroll-mt-24 space-y-4">
            <p className="font-heading font-bold text-xs tracking-[0.2em] text-brand-accent uppercase">
              § 06 · Partners
            </p>
            <h2 className="font-display text-3xl text-warm-800">
              Vendor relationship
            </h2>
            <p className="text-warm-600 leading-relaxed">
              Participating venues are independent businesses. Aranor is not
              their agent and doesn't control the quality, safety, or pricing of
              their goods and services. If you have a concern with a venue,
              please raise it with them first; we're happy to help mediate a
              denied stamp or discount.
            </p>
          </section>

          <section id="ip" className="scroll-mt-24 space-y-4">
            <p className="font-heading font-bold text-xs tracking-[0.2em] text-brand-accent uppercase">
              § 07 · Ownership
            </p>
            <h2 className="font-display text-3xl text-warm-800">
              Intellectual property
            </h2>
            <p className="text-warm-600 leading-relaxed">
              The Aranor name, logo, the stamp artwork, and the tier system are
              ours. Your own stamp and ranking data is yours — if you choose to
              share it publicly, you grant us a non-exclusive license to display
              it on shareable cards.
            </p>
          </section>

          <section id="liability" className="scroll-mt-24 space-y-4">
            <p className="font-heading font-bold text-xs tracking-[0.2em] text-brand-accent uppercase">
              § 08 · Liability
            </p>
            <h2 className="font-display text-3xl text-warm-800">
              Disclaimers &amp; liability
            </h2>
            <p className="text-warm-600 leading-relaxed">
              Aranor is provided "as is". We work hard to keep the service
              running and accurate, but we can't guarantee zero downtime or that
              every venue detail is always current. To the extent the law
              allows, Aranor isn't liable for indirect or consequential losses.
            </p>
          </section>

          <section id="law" className="scroll-mt-24 space-y-4">
            <p className="font-heading font-bold text-xs tracking-[0.2em] text-brand-accent uppercase">
              § 09 · Jurisdiction
            </p>
            <h2 className="font-display text-3xl text-warm-800">
              Governing law
            </h2>
            <p className="text-warm-600 leading-relaxed">
              These Terms are governed by the laws of the Republic of Armenia.
              We'd much rather talk it through than escalate — reach us any time
              at contact@aranortravel.com.
            </p>
            <div className="bg-brand-secondary text-white rounded-2xl p-5">
              <p className="font-heading font-bold text-[10px] tracking-[0.2em] text-brand-accent uppercase">
                Questions before you sign up?
              </p>
              <a
                href="mailto:contact@aranortravel.com"
                className="font-heading font-bold text-brand-primary text-sm break-all"
              >
                contact@aranortravel.com
              </a>{' '}
              <span className="text-warm-500 text-sm">
                — we'd rather answer ten Qs than rescind one stamp.
              </span>
            </div>
          </section>

          <div className="flex flex-col sm:flex-row sm:items-center gap-4 pt-6 border-t border-warm-200">
            <button
              onClick={() => navigate('/register')}
              className="inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-brand-primary text-brand-secondary font-heading font-bold rounded-xl hover:bg-brand-accent transition-all"
            >
              <Check className="w-4 h-4" />
              I agree — create passport
            </button>
            <button
              onClick={() => navigate('/privacy')}
              className="inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-white border border-warm-200 text-warm-700 font-heading font-bold rounded-xl hover:bg-warm-100 transition-all"
            >
              <FileText className="w-4 h-4" />
              Read privacy policy
            </button>
            <span className="text-warm-500 text-sm sm:ml-auto">
              v · 2026.05.19
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Terms;
